import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { ROUTES } from '@/routes/paths';
import { selectActiveRole, useAuthStore } from '@/stores/auth-store';
import type { Role } from '@/types/domain';
import { EmptyState } from '@/components/ui/EmptyState';

/**
 * Restringe una pantalla (o una acción) a un rol del tenant activo.
 *
 * Es el reflejo en el frontend de `requireRole('ADMIN')` del backend
 * (`backend/src/middleware/authorize.ts`): el backend es quien manda (responde 403), esto
 * solo evita mostrar botones que van a fallar. El rol lo trae `GET /api/auth/me`.
 */
export function RequireRole({ role, children }: { role: Role; children: ReactNode }) {
  const activeRole = useAuthStore(selectActiveRole);

  if (activeRole !== role) {
    return (
      <EmptyState
        icon={<ShieldCheck size={22} />}
        title="No tienes permisos para ver esta sección"
        description="Esta pantalla es solo para administradores del negocio activo. Pídele al administrador que cambie tu rol desde Equipo si necesitas acceso."
        action={
          <Link
            to={ROUTES.dashboard}
            className="text-body font-medium text-brand-600 hover:text-brand-700 hover:underline"
          >
            Volver al panel
          </Link>
        }
      />
    );
  }

  return <>{children}</>;
}
